import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Save, Power, Star, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export function GameConfigCard({ owner = false }) {
  const [weeks, setWeeks] = useState([]);
  const [nBulk, setNBulk] = useState({ type: "nominal", value: 25000, free_item_name: "", label: "Hadiah dari Restoran", valid_days: 30 });
  const [pVouchers, setPVouchers] = useState([]);
  const [nPV, setNPV] = useState({ name: "", discount_type: "nominal", discount_value: 10000, point_cost: 100, active: true });

  const loadWeeks = () => api.get("/game/weeks").then((r) => setWeeks(r.data));
  const loadPV = () => api.get("/point-vouchers").then((r) => setPVouchers(r.data));
  useEffect(() => { loadWeeks(); if (owner) loadPV(); }, [owner]);

  const createPV = async () => {
    if (!nPV.name.trim()) { toast.error("Isi nama voucher"); return; }
    if (Number(nPV.discount_value) <= 0 || Number(nPV.point_cost) <= 0) { toast.error("Nilai diskon & biaya poin harus lebih dari 0"); return; }
    try { await api.post("/point-vouchers", nPV); toast.success("E-Voucher poin ditambahkan"); setNPV({ name: "", discount_type: "nominal", discount_value: 10000, point_cost: 100, active: true }); loadPV(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const togglePV = async (v, active) => {
    setPVouchers((xs) => xs.map((x) => (x.id === v.id ? { ...x, active } : x)));
    try { await api.put(`/point-vouchers/${v.id}`, { name: v.name, discount_type: v.discount_type, discount_value: v.discount_value, point_cost: v.point_cost, active }); }
    catch (e) { toast.error(apiError(e)); loadPV(); }
  };
  const delPV = async (id) => { if (!window.confirm("Hapus E-Voucher poin ini?")) return; try { await api.delete(`/point-vouchers/${id}`); toast.success("E-Voucher poin dihapus"); loadPV(); } catch (e) { toast.error(apiError(e)); } };

  const sendBulk = async () => {
    if (nBulk.type !== "free_item" && !nBulk.value) { toast.error("Isi nilai voucher"); return; }
    if (nBulk.type === "free_item" && !nBulk.free_item_name) { toast.error("Isi nama item gratis"); return; }
    if (!window.confirm("Bagikan voucher ini ke SEMUA member sekarang? Tindakan ini tidak bisa dibatalkan.")) return;
    try { const { data } = await api.post("/vouchers/bulk", nBulk); toast.success(`${data.vouchers_created} voucher dibagikan ke semua member`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const patchWeek = (wk, patch) => setWeeks((ws) => ws.map((w) => (w.week === wk ? { ...w, ...patch } : w)));
  const saveWeek = async (w) => {
    try {
      await api.put(`/game/weeks/${w.week}`, { active: w.active, winners_count: Number(w.winners_count), voucher_type: w.voucher_type, voucher_value: Number(w.voucher_value), free_item_name: w.free_item_name });
      loadWeeks(); toast.success("Jadwal minggu disimpan");
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleWeek = async (wk, active) => {
    patchWeek(wk, { active });
    try { await api.put(`/game/weeks/${wk}`, { active }); toast.success(active ? "Game diaktifkan minggu ini" : "Game dinonaktifkan"); }
    catch (e) { toast.error(apiError(e)); loadWeeks(); }
  };
  return (
    <Card className="card-surface rounded-2xl p-5" data-testid="game-config-card">
      <div className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><Gift className="text-crimson" size={18} /> Pengaturan Game & Loyalty</div>
      <p className="mb-4 text-sm text-muted-foreground">Atur jumlah pemenang, tipe & nilai e-voucher <b className="text-gold">per minggu</b> pada jadwal di bawah. Voucher pemenang <b className="text-gold">otomatis dibagikan tiap Senin 00:00 WIB</b> setelah periode 1 minggu berakhir, dan leaderboard reset ke minggu baru.</p>

      {owner && (
        <div className="mb-2 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4" data-testid="bulk-voucher-panel">
          <div className="mb-1 flex items-center gap-2 font-display font-bold text-emerald-400"><Gift size={16} /> Voucher Massal (Hadiah untuk Semua Member)</div>
          <p className="mb-3 text-xs text-muted-foreground">Bagikan voucher gratis ke <b className="text-emerald-400">semua member</b> sekaligus. Voucher ini tampil hijau di "Voucher Saya" member (beda dari voucher menang game).</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Tipe Voucher</Label>
              <Select value={nBulk.type} onValueChange={(v) => setNBulk({ ...nBulk, type: v })}>
                <SelectTrigger data-testid="bulk-type" className="mt-1 h-10 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                  <SelectItem value="percent">Persentase (%)</SelectItem>
                  <SelectItem value="free_item">Item Gratis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">{nBulk.type === "free_item" ? "Nama Item Gratis" : `Nilai (${nBulk.type === "percent" ? "%" : "Rp"})`}</Label>
              {nBulk.type === "free_item"
                ? <Input data-testid="bulk-freeitem" value={nBulk.free_item_name} onChange={(e) => setNBulk({ ...nBulk, free_item_name: e.target.value })} className="mt-1 h-10 bg-neutral-100 border-neutral-200" placeholder="Es Teh Gratis" />
                : <Input data-testid="bulk-value" type="number" value={nBulk.value} onChange={(e) => setNBulk({ ...nBulk, value: Number(e.target.value) })} className="mt-1 h-10 bg-neutral-100 border-neutral-200 font-mono-num" />}
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Label (opsional)</Label>
              <Input data-testid="bulk-label" value={nBulk.label} onChange={(e) => setNBulk({ ...nBulk, label: e.target.value })} className="mt-1 h-10 bg-neutral-100 border-neutral-200" placeholder="Hadiah dari Restoran" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Masa Berlaku</Label>
              <Select value={String(nBulk.valid_days)} onValueChange={(v) => setNBulk({ ...nBulk, valid_days: Number(v) })}>
                <SelectTrigger data-testid="bulk-valid-days" className="mt-1 h-10 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 3, 7, 30, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} Hari</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3">
              <Button data-testid="send-bulk-btn" onClick={sendBulk} className="h-10 bg-emerald-500 font-bold text-black hover:bg-emerald-400"><Gift size={14} className="mr-1" /> Bagikan ke Semua Member</Button>
            </div>
          </div>
        </div>
      )}

      {owner && (
        <div className="mb-2 mt-4 rounded-2xl border border-yellow-400/40 bg-yellow-400/5 p-4" data-testid="point-voucher-panel">
          <div className="mb-1 flex items-center gap-2 font-display font-bold text-yellow-600"><Star size={16} /> E-Voucher Poin (Ditukar dengan Poin Member)</div>
          <p className="mb-3 text-xs text-muted-foreground">Buat variasi voucher yang bisa ditukar member dengan poin. Atur bebas: nominal atau persen. Voucher hasil tukar tampil <b className="text-yellow-600">kuning</b> di "Voucher Saya" & berlaku 7 hari.</p>
          <div className="mb-3 grid gap-3 sm:grid-cols-4">
            <div>
              <Label className="text-[10px] text-muted-foreground">Nama Voucher</Label>
              <Input data-testid="pv-name" value={nPV.name} onChange={(e) => setNPV({ ...nPV, name: e.target.value })} className="mt-1 h-10 bg-neutral-100 border-neutral-200" placeholder="Diskon Spesial" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Jenis Diskon</Label>
              <Select value={nPV.discount_type} onValueChange={(v) => setNPV({ ...nPV, discount_type: v })}>
                <SelectTrigger data-testid="pv-type" className="mt-1 h-10 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                  <SelectItem value="percent">Persentase (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Nilai ({nPV.discount_type === "percent" ? "%" : "Rp"})</Label>
              <Input data-testid="pv-value" type="number" value={nPV.discount_value} onChange={(e) => setNPV({ ...nPV, discount_value: Number(e.target.value) })} className="mt-1 h-10 bg-neutral-100 border-neutral-200 font-mono-num" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Biaya Poin</Label>
              <Input data-testid="pv-cost" type="number" value={nPV.point_cost} onChange={(e) => setNPV({ ...nPV, point_cost: Number(e.target.value) })} className="mt-1 h-10 bg-neutral-100 border-neutral-200 font-mono-num" />
            </div>
            <div className="sm:col-span-4">
              <Button data-testid="pv-add-btn" onClick={createPV} className="h-10 bg-yellow-400 font-bold text-black hover:bg-yellow-300"><Plus size={14} className="mr-1" /> Tambah E-Voucher Poin</Button>
            </div>
          </div>
          <div className="space-y-2" data-testid="pv-list">
            {pVouchers.length === 0 && <p className="text-xs text-muted-foreground">Belum ada E-Voucher poin. Tambahkan di atas.</p>}
            {pVouchers.map((v) => (
              <div key={v.id} data-testid={`pv-row-${v.id}`} className="flex items-center justify-between rounded-xl bg-neutral-100 px-4 py-3">
                <div>
                  <div className="font-semibold text-neutral-900">{v.name} <span className="ml-1 rounded-full bg-yellow-400/20 px-2 py-0.5 text-[10px] font-bold text-yellow-700">{v.discount_type === "nominal" ? `Rp ${Number(v.discount_value).toLocaleString("id-ID")}` : `${v.discount_value}%`}</span></div>
                  <div className="font-mono-num text-[11px] text-muted-foreground">{Number(v.point_cost).toLocaleString("id-ID")} poin</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${v.active ? "text-emerald-500" : "text-muted-foreground"}`}>{v.active ? "Aktif" : "Nonaktif"}</span>
                  <Switch data-testid={`pv-toggle-${v.id}`} checked={v.active} onCheckedChange={(c) => togglePV(v, c)} />
                  <Button data-testid={`pv-del-${v.id}`} onClick={() => delPV(v.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-neutral-200 pt-4">
        <div className="mb-1 flex items-center gap-2 font-display font-bold text-neutral-900"><Power size={16} className="text-gold" /> Jadwal Game Mingguan</div>
        <p className="mb-3 text-xs text-muted-foreground">Aktifkan game per minggu. <b className="text-gold">Default nonaktif</b> — jika minggu ini tidak diaktifkan, member tidak bisa bermain.</p>
        <div className="space-y-3">
          {weeks.map((w) => (
            <div key={w.week} data-testid={`game-week-${w.week}`} className="rounded-xl bg-neutral-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-neutral-900">{w.label} {w.is_current && <span className="ml-1 rounded-full bg-crimson/20 px-2 py-0.5 text-[10px] font-bold text-crimson">MINGGU INI</span>}</div>
                  <div className="font-mono-num text-[11px] text-muted-foreground">{w.week}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${w.active ? "text-emerald-400" : "text-muted-foreground"}`}>{w.active ? "Aktif" : "Nonaktif"}</span>
                  <Switch data-testid={`week-toggle-${w.week}`} checked={w.active} onCheckedChange={(v) => toggleWeek(w.week, v)} />
                </div>
              </div>
              {w.active && (
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Jumlah Pemenang</Label>
                    <Select value={String(w.winners_count)} onValueChange={(v) => patchWeek(w.week, { winners_count: Number(v) })}>
                      <SelectTrigger data-testid={`week-winners-${w.week}`} className="mt-1 h-10 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                      <SelectContent>{[3, 5, 10].map((n) => <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Tipe E-Voucher</Label>
                    <Select value={w.voucher_type} onValueChange={(v) => patchWeek(w.week, { voucher_type: v })}>
                      <SelectTrigger data-testid={`week-type-${w.week}`} className="mt-1 h-10 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                        <SelectItem value="percent">Persentase (%)</SelectItem>
                        <SelectItem value="free_item">Item Gratis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">{w.voucher_type === "free_item" ? "Nama Item Gratis" : `Nilai (${w.voucher_type === "percent" ? "%" : "Rp"})`}</Label>
                    {w.voucher_type === "free_item"
                      ? <Input data-testid={`week-freeitem-${w.week}`} value={w.free_item_name} onChange={(e) => patchWeek(w.week, { free_item_name: e.target.value })} className="mt-1 h-10 bg-neutral-100 border-neutral-200" placeholder="Es Teh Gratis" />
                      : <Input data-testid={`week-value-${w.week}`} type="number" value={w.voucher_value} onChange={(e) => patchWeek(w.week, { voucher_value: Number(e.target.value) })} className="mt-1 h-10 bg-neutral-100 border-neutral-200 font-mono-num" />}
                  </div>
                  <div className="sm:col-span-3">
                    <Button data-testid={`week-save-${w.week}`} onClick={() => saveWeek(w)} className="h-10 bg-crimson font-bold hover:bg-red-500"><Save size={14} className="mr-1" /> Simpan Minggu Ini</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
